import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ApiError,
  ProblemType,
  errorMessage,
  identifierAlreadyYours,
  registrationHeldBy,
  registrationRefusal,
  registrationRunStatus
} from '@/domains/api';
import { registrationApi } from '../api';
import { useAuth } from '../auth-context';
import { AuthShell } from '../components/auth-shell';
import { AuthSplash } from '../components/auth-splash';
import {
  ADD_SURVEY_ROUTE,
  CATALOGUE_ROUTE,
  REGISTRATION_SURVEY_PARAM
} from '../constants';
import {
  type EnteredNumbers,
  type IEnteredNumber,
  type RegistrationRunStore,
  registrationRunStorage
} from '../storage';
import type {
  IAccountCompletion,
  IAddedSurveyRouteState,
  IRegistrationCompletion,
  IRegistrationView,
  RegistrationCaller,
  RegistrationNumber,
  RegistrationStart,
  RegistrationSurveyCode
} from '../types';
import { AnchorStep } from './anchor-step';
import { ChooseNumbers, NoNumber, type NumberTick } from './choose-numbers';
import { ChooseRegistration, linkedStart, startFailure } from './choose-registration';
import { CompleteStep } from './complete-step';
import { ConfirmIdentityStep } from './confirm-identity-step';
import { CreateAccountStep } from './create-account-step';
import { NumberAnchorStep } from './number-anchor-step';
import { RecordStep } from './record-step';
import { RedirectedStep, redirectOf } from './redirected-step';
import { addedNumberText, numbersName, registrationName } from './registration-copy';
import { RouteStep } from './route-step';
import { StepHeading, useStepAction } from './step-parts';
import { type IRouteAnswer, routingAnswers } from './team-routing-question';
import { CheckEmail, NumberRefused, NumberTaken, RunClosed, RunFinished } from './run-ended';

/**
 * `/registration` — a person's registration by the numbers they hold (SR-487, the client's
 * wireframes "Proposed sign-up flow"), and signed in, at `/portal/surveys/add`, a firm's or a
 * team's survey run too — over survey-api's `/auth/registrations`.
 *
 * **The anonymous door walks the wireframes' pages, and says what each says.** Page 01 asks
 * which numbers the registrant holds (`ChooseNumbers`); then for each number, CRD first, its
 * anchor (02) and "Is this you?" (03); the taken number's end (04); the none path's notice (07),
 * drawn before any run starts; the account (08); and the invitation on its way (09). Which page
 * comes next is the view's `step` and `number`, never this page's.
 *
 * **The run is the server's, and this page holds none of it.** Every call answers the
 * `RegistrationView` of the step the run is now on, and the page replaces what it holds with
 * that, wholesale — it merges nothing and derives no step. The onboarding study at
 * `/preview/onboarding` draws the same screens, but its `flow.ts` is a reducer over invented
 * data that *decides* where a run goes, and a copy of that here would be a second evaluator
 * of the flow beside the one that creates the account. Its layout and copy were the starting
 * point; its state was not.
 *
 * **A reload resumes the run.** The run id is kept per tab — and per account, signed in
 * (`registrationRunStorage`) — and read back through `GET /auth/registrations/{runId}` on load,
 * so a tab picks up on the step the run is on rather than the one it was showing. The id is
 * never in the URL, which stays `/registration` (or `/portal/surveys/add`) from the first screen to the
 * last; the storage's own comment says why.
 *
 * **Two things are the page's and not the run's.** A person's numbers as entered (`entered`),
 * which "Is this you?" names and the view does not carry, kept beside the run id. And a team's
 * answers on the route step: survey-api keeps none between calls, so the answers given so far
 * travel with the view they produced (`given`) and are posted whole each time. Every other way a
 * view arrives — a read, a step back, another tab's step — carries none, which is the route step
 * starting again from its first question: a resumed tab cannot know what was answered, and the
 * step says so.
 *
 * **Refusals split in two, and the split is where they are handled** (R6). What ends or moves
 * the *run* — 404, `registration-out-of-step` in each of its three run statuses (an abandoned
 * run that stopped on `redirected` was sent to another survey, not closed — and since the 409
 * names no destination, the survey the tab kept beside the run says which: B1-1),
 * and the `identifier_unavailable` refusal that ends it — is `interruption`'s, here, and is the
 * same answer whichever step or resume it arrived on. What is about a *value* — the address
 * refused, a `ValidationProblem`, the server out of reach — goes back to the step that sent
 * it and is drawn beside what the registrant typed, because the run it was sent on is still
 * exactly where it was.
 *
 * **The completion issues no session** (R5). survey-api answers 201 and signs nobody in: the
 * account signs in at Invent's IdP, through the invitation the completion wrote for its address.
 * So the page ends on page 09 (`CheckEmail`), which says where the invitation went and when it
 * expires — never back into a run that has finished.
 *
 * **Two modes, decided by the route** (the teams onboarding plan's R7). Anonymous,
 * `/registration` is mounted beside `/login` rather than under `ProtectedRoute`: there is no
 * account until the run's last step makes one, and every call it makes goes out without a
 * bearer. A visitor who arrives there **with** a session is sent to `/portal/surveys/add` instead,
 * keeping the query (`?survey=`): survey-api binds a run to whatever session
 * starts it, so an anonymous registration cannot be run over a token, and one run without it
 * would mint a second login and throw the first session away as it signed in. `/portal/surveys/add` is
 * the same page in `account` mode, inside the respondent area: every call carries the bearer,
 * the run is kept under the account's own key, the completion sends the fields alone and lands
 * on the catalogue naming what was added, and a number the account already holds is said as
 * the account's own (`identifier-already-yours`) with the run left at the anchor for another.
 * At a person's completion that refusal, and `several-persons`, leave the run no step to take
 * (Q24): the page forgets it and draws the chooser under the notice the start draws for each.
 *
 * **Signed in, a registration can be picked by the link** (`?survey=TEAMS`, …) rather than on
 * the chooser (ruling 11): `RIA_FIRM` and `TEAMS` start that survey's run, `FA` and `FSP` the
 * matching number's (`linkedStart`). Anonymous, the parameter is dropped and page 01 drawn:
 * survey-api starts a survey's run signed in only (PFR D4). Signed in, a run is started for it on
 * arrival unless the tab already holds one, and the parameter is dropped as it is read, so a
 * reload resumes that run rather than starting a second. A tab that holds **another** run is
 * asked which (the owner's ruling): continue the run it holds, or start the link's — abandoning
 * the held one first, in survey-api, so nothing it was given lingers. Neither is chosen silently:
 * resuming would drop the link without a word, and starting would throw away a run the
 * registrant may be half-way through. A link to the registration the tab is already walking
 * resumes it without asking, since both answers are the same.
 */

/** Which caller the page registers for — `/registration`'s anonymous visitor, or the account
 *  signed in on `/portal/surveys/add`, whose own participants only the survey domain can name (it is
 *  the one reader of `/my/participants`), so it hands the page how to draw one. */
export type RegistrationMode =
  | { caller: 'anonymous' }
  | {
      caller: 'account';
      accountId: string;
      /** The account's participant that already holds a number, named and linked to where it
       *  starts — `identifier-already-yours`' `participantId`. */
      ownParticipant: (participantId: string) => React.ReactNode;
    };

type PageState =
  | { kind: 'reading' }
  /** Page 01 anonymous — `ticks` what Back brought back with it — and signed in the chooser;
   *  `yours` the account's participant a person's completion found holding the number (Q24). */
  | {
      kind: 'choosing';
      notice?: React.ReactNode;
      ticks?: readonly NumberTick[];
      yours?: string;
    }
  /** Page 07, "None of the above": no run started yet. */
  | { kind: 'none' }
  /** `given` is the route step's answers so far, beside the view they produced. */
  | {
      kind: 'step';
      view: IRegistrationView;
      given?: readonly IRouteAnswer[];
      notice?: React.ReactNode;
      /** Signed in: the account's participant that already holds the number just offered. */
      yours?: string;
    }
  /** Refused over a number another account holds: `heldBy` the refusal's masked addresses, and
   *  on a person's run the number in hand — page 04 — where a firm's or a team's has none. */
  | {
      kind: 'refused';
      runId: string;
      heldBy: string[];
      person: Nullable<{ number: Nullable<RegistrationNumber> }>;
    }
  /** The anonymous completion's 201 (page 09): the address it was made with, the test inbox
   *  named, and when the invitation to sign in expires. */
  | { kind: 'created'; email: string; deliverTo?: string; expiresAt: Nullable<string> }
  | { kind: 'finished' }
  | { kind: 'closed' }
  /** A run sent to another survey, come back to after the fact: the run is over, and where it
   *  went is the kept survey's to say (`redirectOf`) — null where none was kept. */
  | { kind: 'redirected'; destination: Nullable<string> }
  /** A link's `?survey=` for another registration than the run the tab holds, which is `view`. */
  | { kind: 'held'; view: IRegistrationView; linked: RegistrationStart }
  /** The stored run could not be read, for a reason other than survey-api not knowing it. */
  | { kind: 'unreadable'; message: string };

const FORGOTTEN = 'We could not find that registration any more, so it has to start again.';
const MOVED_ON =
  'This registration had moved on since the page was loaded — this is the step it is on now.';

/** The stored run could not be read, and the answer says nothing about the run itself. */
function unreadable(error: unknown): PageState {
  return { kind: 'unreadable', message: errorMessage(error, 'Could not load your registration.') };
}

/** Whether a link's start is the run the tab holds: the same survey, or the same numbers. */
function sameStart(start: RegistrationStart, view: IRegistrationView): boolean {
  if ('survey' in start) return view.survey === start.survey;
  const held = view.numbers;
  return (
    held !== null &&
    held.length === start.numbers.length &&
    start.numbers.every((number) => held.includes(number))
  );
}

/** A registration by what it registers, where `HeldRun` names two apart. */
function startName(start: RegistrationStart): string {
  return 'survey' in start ? registrationName(start.survey) : numbersName(start.numbers);
}

function runName(view: IRegistrationView): string {
  return view.numbers !== null ? numbersName(view.numbers) : registrationName(view.survey ?? '');
}

/** The run's transport and its store — both the caller's (`RegistrationMode`). */
interface IRunContext {
  api: ReturnType<typeof registrationApi>;
  runs: RegistrationRunStore;
}

/**
 * What an error ends the run's screen in, or null when the step that sent the request draws
 * it itself. Forgets the stored run on every answer that says it takes no more steps.
 *
 * A 404 is also what a run **bound to another session** answers, whoever asks — which is
 * exactly "we could not find it" from where this tab stands, and says nothing of whose it is.
 *
 * `in_progress` is the one out-of-step status that is not an ending: the run went on without
 * this tab (another tab, a retried submit), so it is read back and drawn where it actually is
 * — `step` and `status` travel on the 409 precisely so a client re-renders instead of
 * starting over. The read is `GET`, which never answers out-of-step for a run in progress, so
 * the recursion below settles in one level.
 */
async function interruption(
  error: unknown,
  runId: string,
  { api, runs }: IRunContext
): Promise<Nullable<PageState>> {
  if (error instanceof ApiError && error.status === 404) {
    runs.remove();
    return { kind: 'choosing', notice: FORGOTTEN };
  }

  switch (registrationRunStatus(error)) {
    case 'in_progress':
      try {
        return { kind: 'step', view: await api.readRegistration(runId), notice: MOVED_ON };
      } catch (readError) {
        return (await interruption(readError, runId, { api, runs })) ?? unreadable(readError);
      }
    case 'completed':
      runs.remove();
      return { kind: 'finished' };
    case 'abandoned': {
      const held = runs.get();
      runs.remove();
      // survey-api abandons a redirected run as it answers the step, so "abandoned" alone
      // cannot tell a run sent elsewhere from one somebody closed; the step it stopped on can.
      // Where it was sent the 409 does not say, and the survey the run started on does.
      if (!(error instanceof ApiError && error.problem?.step === 'redirected')) {
        return { kind: 'closed' };
      }
      const startedOn = held?.runId === runId ? held.survey : null;
      return { kind: 'redirected', destination: redirectOf(startedOn) };
    }
  }

  if (registrationRefusal(error) === 'identifier_unavailable') {
    runs.remove();
    return { kind: 'refused', runId, heldBy: registrationHeldBy(error), person: null };
  }
  return null;
}

/**
 * The route's element. Anonymous, it waits for the session check and sends a signed-in visitor
 * to `/portal/surveys/add`, keeping the query that picked a survey.
 */
export function RegistrationPage({ mode }: { mode: RegistrationMode }) {
  const { status } = useAuth();
  const { search } = useLocation();

  if (mode.caller === 'account') return <RegistrationFlow mode={mode} />;
  if (status === 'pending') return <AuthSplash />;
  if (status === 'authenticated') {
    return <Navigate to={{ pathname: ADD_SURVEY_ROUTE, search }} replace />;
  }
  return <RegistrationFlow mode={mode} />;
}

function RegistrationFlow({ mode }: { mode: RegistrationMode }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const accountId = mode.caller === 'account' ? mode.accountId : null;
  const context = React.useMemo<IRunContext>(
    () => ({ api: registrationApi(mode.caller), runs: registrationRunStorage(accountId) }),
    [mode.caller, accountId]
  );
  const { api, runs } = context;

  // Read once, on arrival: the survey a link picked, and whether the tab already holds a run —
  // the link's survey is started only where it holds none, and only signed in (the module's
  // JSDoc). Signed in, a parameter that names no registration is left where it is; the anonymous
  // door drops any, unread.
  const [arrival] = React.useState(() => {
    const survey = params.get(REGISTRATION_SURVEY_PARAM);
    const linked = mode.caller === 'account' ? linkedStart(survey) : null;
    return {
      linked,
      dropped: mode.caller === 'account' ? linked !== null : survey !== null,
      holding: runs.get() !== null
    };
  });
  const picked = arrival.holding ? null : arrival.linked;
  // The link's survey, until the held run's first read answers whether it is another survey's.
  const heldLink = React.useRef(arrival.holding ? arrival.linked : null);
  const [state, setState] = React.useState<PageState>(() =>
    arrival.holding || picked ? { kind: 'reading' } : { kind: 'choosing' }
  );
  // Bumped by "Try again", which is the only thing besides the first render that reads.
  const [reads, setReads] = React.useState(0);
  // A strict-mode remount runs the effect twice; the run it starts must be one.
  const startedPicked = React.useRef(false);
  // A person's numbers as entered at their anchors, for the pages that name them (the module's
  // JSDoc) — kept beside the run, so a reload still names them.
  const [entered, setEntered] = React.useState<EnteredNumbers>(() => runs.get()?.entered ?? {});

  const start = async (body: RegistrationStart) => {
    const view = await api.startRegistration(body);
    runs.set({ runId: view.runId, survey: view.survey, numbers: view.numbers, entered: {} });
    setEntered({});
    setState({ kind: 'step', view });
  };

  const startSurvey = (survey: RegistrationSurveyCode) => start({ survey });

  /** Keeps a number as it is sent, before survey-api answers: whatever the answer is — "Is this
   *  you?", the taken number's end, a retry — names it. */
  const remember = (view: IRegistrationView, number: IEnteredNumber) => {
    if (view.number === null) return;
    const next = { ...entered, [view.number]: number };
    setEntered(next);
    const held = runs.get();
    if (held?.runId === view.runId) runs.set({ ...held, entered: next });
  };

  React.useEffect(() => {
    if (!arrival.dropped || startedPicked.current) return;
    startedPicked.current = true;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(REGISTRATION_SURVEY_PARAM);
        return next;
      },
      { replace: true }
    );
    if (!picked) return;
    start(picked).catch((error: unknown) =>
      setState({ kind: 'choosing', notice: startFailure(error) })
    );
    // Once, on arrival: `arrival` never changes, and `start` is this render's.
  }, [arrival]);

  React.useEffect(() => {
    const runId = runs.get()?.runId;
    if (!runId) return undefined;

    const controller = new AbortController();
    api.readRegistration(runId, controller.signal).then(
      (view) => {
        if (controller.signal.aborted) return;
        const linked = heldLink.current;
        heldLink.current = null;
        setState(
          linked !== null && !sameStart(linked, view)
            ? { kind: 'held', view, linked }
            : { kind: 'step', view }
        );
      },
      async (error: unknown) => {
        if (controller.signal.aborted) return;
        // The run held is not one to resume, so there is nothing to ask the link about.
        const linked = heldLink.current;
        heldLink.current = null;
        const next = await interruption(error, runId, context);
        if (controller.signal.aborted) return;
        // A held run that took its last step — redirected, refused, finished, closed, gone — is
        // no obstacle to the link, which starts as it would on a tab holding none. Since a
        // redirected run stays held to be read back (B1-1), a link would otherwise land on the
        // old run's redirect and its survey be dropped. A run read back in progress is drawn.
        if (linked !== null && next?.kind !== 'step') {
          start(linked).catch((failure: unknown) =>
            setState({ kind: 'choosing', notice: startFailure(failure) })
          );
          return;
        }
        setState(next ?? unreadable(error));
      }
    );
    return () => controller.abort();
  }, [reads, context]);

  /**
   * "Start the … registration instead": the held run abandoned in survey-api, then the link's
   * started. A held run that has ended since it was read — finished, closed or gone — is no
   * obstacle, since there is nothing left to abandon; any other refusal stops the switch, with
   * the held run still there to continue.
   */
  const switchRun = async (runId: string, linked: RegistrationStart) => {
    try {
      await api.abandonRegistration(runId);
    } catch (error) {
      const status = registrationRunStatus(error);
      const ended =
        (error instanceof ApiError && error.status === 404) ||
        status === 'completed' ||
        status === 'abandoned';
      if (!ended) throw error;
    }
    runs.remove();
    await start(linked);
  };

  /** Back to page 01 (signed in, the chooser), with `ticks` ticked where Back brought some. */
  const restart = (ticks?: readonly NumberTick[]) => setState({ kind: 'choosing', ticks });

  /** `restart` for a run this tab still holds: forgotten first, so a reload cannot resume it. */
  const startOver = (ticks?: readonly NumberTick[]) => {
    runs.remove();
    restart(ticks);
  };

  /**
   * One request against the run. What it answers about the run as a whole replaces the
   * step; anything else is rethrown for the step to draw beside what was typed — except a
   * number the signed-in account already holds, which is the page's to say (it names one of
   * the account's participants) and leaves the run exactly where it was.
   *
   * At a person's completion, that refusal and `several-persons` are about the account, not
   * the run: survey-api leaves the run on its last step, which the same press would only be
   * refused again on. So the run ends here (Q24) — forgotten, so a reload cannot resume the
   * dead step, and the chooser drawn under the notice the start draws for each refusal.
   */
  const attempt = async (view: IRegistrationView, action: () => Promise<void>) => {
    const { runId } = view;
    // What was said of the last number offered is not about the one being sent now.
    setState((current) =>
      current.kind === 'step' && current.yours ? { ...current, yours: undefined } : current
    );
    try {
      await action();
    } catch (error) {
      const yours = identifierAlreadyYours(error);
      const several = error instanceof ApiError && error.isProblem(ProblemType.severalPersons);
      if (view.numbers !== null && view.step === 'complete' && (yours !== null || several)) {
        runs.remove();
        setState(
          yours !== null
            ? { kind: 'choosing', yours }
            : { kind: 'choosing', notice: startFailure(error) }
        );
        return;
      }
      if (yours !== null) {
        setState((current) => (current.kind === 'step' ? { ...current, yours } : current));
        return;
      }
      const next = await interruption(error, runId, context);
      if (!next) throw error;
      // A person's refused number is page 04's, which names it.
      setState(
        next.kind === 'refused' && view.numbers !== null
          ? { ...next, person: { number: view.number } }
          : next
      );
    }
  };

  /** Draws the view a transition answered. A `redirected` one ends the run as it arrives —
   *  survey-api abandoned it in the same request — and the tab still holds it, so that a reload
   *  reads it back and is told, by the 409 and the kept survey, where it went (`interruption`);
   *  the next run started replaces it. */
  const advance = (
    view: IRegistrationView,
    transition: () => Promise<IRegistrationView>,
    given?: readonly IRouteAnswer[]
  ) =>
    attempt(view, async () => {
      const view = await transition();
      setState({ kind: 'step', view, given });
    });

  /** Everything a step can ask of the run, each bound to the run on screen. */
  const handlersFor = (view: IRegistrationView): IStepHandlers => {
    const { runId, survey } = view;
    return {
      onAnchor: (number) => {
        remember(view, number);
        // A type is named only where the run reads more than one; else survey-api reads its own.
        const idType = view.idTypes.length > 1 ? number.idType : undefined;
        return advance(view, () =>
          api.submitAnchor(runId, number.value, idType, number.issuingState)
        );
      },
      onNoAnchor: () => advance(view, () => api.declareNoAnchor(runId)),
      onDecide: (confirmed, sameTeam) =>
        advance(view, () => api.decideIdentity(runId, confirmed, sameTeam)),
      onRoute: (given) =>
        advance(view, () => api.submitRouting(runId, routingAnswers(given)), given),
      onRedirect: () => advance(view, () => api.redirectRegistration(runId)),
      // A new run, from its first step: nothing typed in this one was the other survey's.
      onRegister: startSurvey,
      onRevise: () => advance(view, () => api.reviseRegistration(runId)),
      onChoose: () => startOver(),
      // Page 01 again, a person's numbers still ticked.
      onBack: () =>
        attempt(view, async () => {
          await api.abandonRegistration(runId);
          startOver(view.numbers ?? undefined);
        }),
      onComplete: (completion) =>
        attempt(view, async () => {
          const completed = await api.completeRegistration(runId, completion);
          runs.remove();
          // The anonymous end: the account exists, and the completion signed nobody in.
          if ('email' in completion) {
            setState({
              kind: 'created',
              email: completion.email.trim(),
              deliverTo: completion.deliverTo,
              expiresAt: completed.invitationExpiresAt ?? null
            });
            return;
          }
          // Straight to the catalogue, not through the index route's redirect, which would
          // drop the state that names what was added: a person's numbers as entered (Q11), or
          // the survey a firm's or a team's run was for.
          const numbers = (view.numbers ?? []).flatMap((number) => {
            const held = entered[number];
            return held ? [addedNumberText(held)] : [];
          });
          const landed: IAddedSurveyRouteState =
            view.numbers !== null
              ? numbers.length > 0
                ? { added: { numbers } }
                : {}
              : {
                  added: {
                    survey: survey ?? '',
                    destinationSurvey: completed.destinationSurvey ?? '',
                    redirected: completed.redirected
                  }
                };
          navigate(CATALOGUE_ROUTE, { replace: true, state: landed });
        })
    };
  };

  const Frame = mode.caller === 'account' ? AccountFrame : DoorFrame;

  return (
    <Frame>
      {state.kind === 'reading' && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading your registration…
        </p>
      )}

      {(state.kind === 'choosing' || state.kind === 'step') && state.notice && (
        <p
          role="status"
          className={cn(
            'mb-5 rounded-md border border-[var(--color-ocean-blue)] px-3 py-2 text-sm',
            'bg-[var(--color-slight-blue)]/40 text-[var(--color-royal-blue)]'
          )}
        >
          {state.notice}
        </p>
      )}

      {(state.kind === 'choosing' || state.kind === 'step') &&
        state.yours &&
        mode.caller === 'account' && (
          <div className="mb-5">{mode.ownParticipant(state.yours)}</div>
        )}

      {state.kind === 'choosing' &&
        (mode.caller === 'account' ? (
          <ChooseRegistration onChoose={start} />
        ) : (
          <ChooseNumbers
            ticks={state.ticks}
            onStart={(numbers) => start({ numbers })}
            onNone={() => setState({ kind: 'none' })}
          />
        ))}

      {state.kind === 'none' && (
        <NoNumber onContinue={() => start({ numbers: [] })} onBack={() => restart(['none'])} />
      )}

      {state.kind === 'step' && (
        <StepScreen
          // Keyed by the step and the number in hand, so a step's own typing does not survive
          // into the next one — while a first `not_found`, which answers the same step, keeps
          // the number there to be checked.
          key={`${state.view.step}:${state.view.number ?? ''}`}
          view={state.view}
          given={state.given ?? []}
          entered={entered}
          caller={mode.caller}
          {...handlersFor(state.view)}
        />
      )}

      {state.kind === 'redirected' && (
        <RedirectedStep
          caller={mode.caller}
          destination={state.destination}
          onRegister={startSurvey}
          onChoose={() => restart()}
        />
      )}

      {state.kind === 'held' && (
        <HeldRun
          held={runName(state.view)}
          linked={startName(state.linked)}
          onContinue={() => setState({ kind: 'step', view: state.view })}
          onSwitch={() => switchRun(state.view.runId, state.linked)}
        />
      )}

      {state.kind === 'refused' &&
        (state.person ? (
          <NumberTaken
            number={state.person.number ? entered[state.person.number] : undefined}
            heldBy={state.heldBy}
            onRestart={() => restart()}
          />
        ) : (
          <NumberRefused runId={state.runId} heldBy={state.heldBy} onRestart={() => restart()} />
        ))}
      {state.kind === 'created' && (
        <CheckEmail email={state.email} deliverTo={state.deliverTo} expiresAt={state.expiresAt} />
      )}
      {state.kind === 'finished' && <RunFinished caller={mode.caller} />}
      {state.kind === 'closed' && <RunClosed onRestart={() => restart()} />}

      {state.kind === 'unreadable' && (
        <>
          <h1 className="text-2xl text-[var(--color-royal-blue)]">
            We could not load your registration
          </h1>
          <p role="alert" className="mt-2 mb-6 text-sm text-destructive">
            {state.message}
          </p>
          <div className="grid gap-3">
            <Button
              onClick={() => {
                setState({ kind: 'reading' });
                setReads((count) => count + 1);
              }}
            >
              Try again
            </Button>
            {/* Forgets the run rather than abandoning it: a run this page cannot read is one
                it cannot reliably abandon either, and survey-api's sweep ends a run nobody
                comes back to. */}
            <Button variant="outline" onClick={() => startOver()}>
              Start a new registration
            </Button>
          </div>
        </>
      )}
    </Frame>
  );
}

/** `/registration`'s frame: the front door, titled for the account the run makes. */
function DoorFrame({ children }: { children: React.ReactNode }) {
  return <AuthShell title="Create an account">{children}</AuthShell>;
}

/** `ADD_SURVEY_ROUTE`'s frame: the registration's one card, since the steps are the same
 *  screens as `/registration`'s, inside the Advisor Portal. The page header above it is the
 *  survey domain's (`AddSurveyPage`): the portal's pages import this domain's barrel, so drawing
 *  it from here would be an import cycle, and the page a blank screen. */
function AccountFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[28rem] px-6 pt-6 pb-16">
      <div className="rounded-[0.625rem] border border-border bg-card p-8">{children}</div>
    </div>
  );
}

/**
 * A link for one registration, followed in a tab part-way through another: which one to go on with
 * is the registrant's to say (the module's JSDoc), each named by what it registers. Continuing
 * draws the held run's step as it was read; starting the link's abandons the held run first, and a
 * failure there — or of the start — is said here, with both choices still offered.
 */
function HeldRun({
  held,
  linked,
  onContinue,
  onSwitch
}: {
  held: string;
  linked: string;
  onContinue: () => void;
  onSwitch: () => Promise<void>;
}) {
  const { pending, error, run } = useStepAction();

  return (
    <>
      <StepHeading
        title="You are part-way through a registration"
        lede={
          `The ${held} registration is in progress in this tab, and the link you followed ` +
          `starts the ${linked} registration. Go on with the one in progress, or start the ` +
          'other instead — the one in progress is then closed, and nothing it was given is kept.'
        }
      />

      {error !== null && (
        <p role="alert" className="mb-4 text-sm font-medium text-destructive">
          {startFailure(error)}
        </p>
      )}

      <div className="grid gap-3">
        <Button disabled={pending} onClick={onContinue}>
          Continue the {held} registration
        </Button>
        <Button variant="outline" disabled={pending} onClick={() => void run(onSwitch)}>
          {pending ? 'Starting…' : `Start the ${linked} registration instead`}
        </Button>
      </div>
    </>
  );
}

interface IStepHandlers {
  onAnchor: (number: IEnteredNumber) => Promise<void>;
  onNoAnchor: () => Promise<void>;
  onBack: () => Promise<void>;
  onDecide: (confirmed: boolean, sameTeam?: string) => Promise<void>;
  onRoute: (given: readonly IRouteAnswer[]) => Promise<void>;
  onRevise: () => Promise<void>;
  onComplete: (completion: IRegistrationCompletion | IAccountCompletion) => Promise<void>;
  onRedirect: () => Promise<void>;
  onRegister: (survey: RegistrationSurveyCode) => Promise<void>;
  onChoose: () => void;
}

/**
 * One screen per step of survey-api's five, chosen by `view.step` — and on a person's run by the
 * number in hand too, whose pages are the wireframes' — and by nothing else. A step this build has
 * no screen for throws rather than rendering nothing: a blank registration is a dead end nobody
 * reports, and a sixth step is a contract change to meet, not to pass over.
 *
 * Signed in, a person's run walks the same pages to its completion, which is `CompleteStep`'s
 * (fields alone): the account exists, and page 08 is the one that makes it.
 */
function StepScreen({
  view,
  given,
  entered,
  caller,
  ...on
}: IStepHandlers & {
  view: IRegistrationView;
  given: readonly IRouteAnswer[];
  entered: EnteredNumbers;
  caller: RegistrationCaller;
}) {
  const person = view.numbers !== null;
  switch (view.step) {
    case 'anchor':
      if (person) {
        return (
          <NumberAnchorStep
            view={view}
            caller={caller}
            entered={view.number ? entered[view.number] : undefined}
            onSubmit={on.onAnchor}
            onBack={on.onBack}
          />
        );
      }
      return (
        <AnchorStep
          view={view}
          onSubmit={(value, idType) =>
            on.onAnchor({ value, idType: idType ?? view.idTypes[0] ?? '' })
          }
          onNoAnchor={on.onNoAnchor}
          onRedirect={on.onRedirect}
          onBack={on.onBack}
        />
      );
    case 'confirm_identity':
      if (person) {
        const number = view.number ? entered[view.number] : undefined;
        return <RecordStep view={view} entered={number} onDecide={on.onDecide} />;
      }
      return <ConfirmIdentityStep view={view} onDecide={on.onDecide} />;
    case 'route':
      return <RouteStep view={view} given={given} onRoute={on.onRoute} onRevise={on.onRevise} />;
    case 'complete':
      if (person && caller === 'anonymous') {
        return <CreateAccountStep view={view} onComplete={on.onComplete} />;
      }
      return <CompleteStep view={view} onComplete={on.onComplete} onRevise={on.onRevise} />;
    case 'redirected':
      return (
        <RedirectedStep
          caller={caller}
          destination={view.destinationSurvey}
          onRegister={on.onRegister}
          onChoose={on.onChoose}
        />
      );
    default: {
      const unknown: never = view.step;
      throw new Error(`registration step '${String(unknown)}' has no screen`);
    }
  }
}
